export interface ImageOptimizationOptions {
  src: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  sizes?: string;
  className?: string;
}

export function getOptimizedImageProps(options: ImageOptimizationOptions) {
  const { src, alt, loading = 'lazy', sizes, className } = options;
  
  return {
    src,
    alt,
    loading,
    ...(sizes && { sizes }),
    ...(className && { className }),
    decoding: 'async' as const,
  };
}

export function generateSrcSet(baseUrl: string, widths: number[]): string {
  return widths
    .map(width => `${baseUrl}?w=${width} ${width}w`)
    .join(', ');
}

/**
 * Generates a srcSet using resolution-based file variants.
 * Expects files named: image-400.webp, image-600.webp, image.webp (full).
 * Only generates srcSet for paths known to have variants.
 */
export function generateResolutionSrcSet(
  src: string,
  widths: number[] = [400, 600],
): string {
  return widths
    .concat([0]) // full-size marker
    .map((w) => {
      if (w === 0) return `${src} ${widths[widths.length - 1] + 200}w`;
      const variant = src.replace(/\.(webp|png|jpg|jpeg)$/, `-${w}.$1`);
      return `${variant} ${w}w`;
    })
    .join(', ');
}

/** Check if the path is within a directory that has pre-generated responsive variants */
export function hasResponsiveVariants(src: string): boolean {
  return src.startsWith('/images/timeline/');
}

export function generatePlaceholder(width: number, height: number, color: string = '#e5e7eb'): string {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'%3E%3Crect width='${width}' height='${height}' fill='${color}'/%3E%3C/svg%3E`;
}

export function calculateReadingTime(text: string): number {
  const wordsPerMinute = 200;
  const wordCount = text.trim().split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
}

export function isImageBelowFold(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return rect.top > window.innerHeight;
}

/**
 * Generates a resized image URL via the /img/resize endpoint.
 * Use this for dynamically uploaded images (logos, settings images) that 
 * need to be served at specific widths.
 */
export function resizedImageUrl(path: string, width: number): string {
  return `/img/resize?src=${encodeURIComponent(path)}&w=${width}`;
}

/**
 * Generates src + srcSet props for resized logos at 1x and 2x densities.
 */
export function resizedLogoSrcSet(
  path: string,
  w1x: number = 200,
  w2x: number = 400,
): { src: string; srcSet: string } {
  return {
    src: resizedImageUrl(path, w2x),
    srcSet: `${resizedImageUrl(path, w1x)} 1x, ${resizedImageUrl(path, w2x)} 2x`,
  };
}

export const IMAGE_WIDTHS = {
  thumbnail: [150, 300],
  card: [300, 600, 900],
  hero: [640, 1024, 1536, 2048],
} as const;
